/**
 * "Which saved version IS this service's interface?" — resolved once (task 818).
 *
 * Two surfaces now render the same generated interface: the Interface tab, where
 * it is built and its history is browsable, and the Developer Info tab's UI
 * section, where a developer reads what the interface is. Both have to answer
 * the same question first, and the answer is not a one-liner:
 *
 *   1. `GET /generated-endpoint-uis` returns **parent** UIs with their versions
 *      nested. `source` and `prompt` exist only on a version, so a parent record
 *      is never previewable — reading source off the parent is exactly the
 *      defect task 396 fixed, and it presented as "this service has no
 *      interface" on a service that had one.
 *   2. The current interface is the parent's HEAD version. `headVersionId` is
 *      authoritative; the newest-first ordering is the fallback for a parent
 *      that carries no head pointer.
 *
 * Spelled out in one place so the two surfaces cannot disagree about which
 * version a service's interface is — a drift that would show as the Developer
 * Info tab previewing one thing and the Interface tab another, with nothing on
 * either page explaining why.
 *
 * The hook owns resolution only. Neither the sandbox nor any chrome lives here:
 * the Interface tab wraps its preview in a card with a version rail and a
 * Hide/Show control, and Developer Info deliberately has none of that.
 */
import { useMemo } from 'react';
import { useGeneratedUis } from '@/hooks/useGeneratedUis';
import { flattenVersions, resolveHeadVersion } from '@/lib/generatedUi';
import type { GeneratedEndpointUi, GeneratedEndpointUiVersion } from '@/types/uiComposer';

export interface ServiceInterfaceVersions {
  /** The parent UI records, as the API returns them. */
  uis: GeneratedEndpointUi[];
  /** Every version across every parent — what the Interface tab's rail lists. */
  versions: GeneratedEndpointUiVersion[];
  /**
   * The version that IS this service's interface right now: the first parent's
   * head, falling back to the last flattened version for a parent with no head
   * pointer. `undefined` when the service has no interface at all.
   */
  current: GeneratedEndpointUiVersion | undefined;
  /** The head version specifically — what the "Current" marker points at. */
  head: GeneratedEndpointUiVersion | undefined;
  isLoading: boolean;
}

export function useServiceInterfaceVersions(
  endpointId: string | undefined
): ServiceInterfaceVersions {
  const { data, isLoading } = useGeneratedUis(endpointId);

  const uis = useMemo(() => data?.data ?? [], [data]);
  const versions = useMemo(() => flattenVersions(uis), [uis]);
  const head = uis.length > 0 ? resolveHeadVersion(uis[0]) : undefined;

  return {
    uis,
    versions,
    head,
    current: head ?? versions[versions.length - 1],
    isLoading,
  };
}
